import { useId } from 'react';

import type {
  CreditParameterSpec,
  CreditParameterSet,
  CreditRuleSpec,
} from '@/shared/api/resources';
import { Badge } from '@/shared/ui/Badge';

import { EditorDoParametro } from './editores';
import { ValorLegivel } from './ValorLegivel';
import {
  iguais,
  rotuloDaClassificacao,
  tomDaClassificacao,
  type Resultado,
} from './valores';

const titulo = 'text-2xs font-semibold uppercase tracking-wide text-ink-500';

/**
 * Um parametro: o que e, de onde veio o valor, a classificacao da fonte, o valor da
 * versao ativa e o do rascunho, lado a lado. Com permissao e rascunho aberto, o lado
 * do rascunho e o editor.
 */
export function CartaoDoParametro({
  spec,
  ativa,
  rascunho,
  editavel,
  rotulos,
  regras,
  pendente,
  erroDoServidor,
  onChange,
}: {
  spec: CreditParameterSpec;
  ativa: CreditParameterSet | undefined;
  rascunho: CreditParameterSet | undefined;
  editavel: boolean;
  rotulos: Record<string, string>;
  regras: CreditRuleSpec[];
  pendente: Resultado | undefined;
  erroDoServidor: string | undefined;
  onChange: (resultado: Resultado) => void;
}) {
  const id = useId();
  const valorAtivo = ativa?.values?.[spec.key];
  const valorRascunho = rascunho?.values?.[spec.key];
  const diferente =
    ativa?.values !== undefined &&
    rascunho?.values !== undefined &&
    !iguais(valorAtivo, valorRascunho);

  return (
    <section
      aria-labelledby={id}
      className="flex flex-col gap-2 border-t border-line py-4 first:border-t-0 first:pt-0"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h4 id={id} className="text-sm font-semibold text-ink-900">
          {spec.label}
        </h4>
        <code className="text-2xs text-ink-500">{spec.key}</code>
        <Badge tone={tomDaClassificacao(spec.classification)}>
          {rotuloDaClassificacao(spec.classification)}
        </Badge>
        {diferente ? <Badge tone="brand">Diferente da ativa</Badge> : null}
        {pendente ? <Badge tone="warning">Nao salvo</Badge> : null}
      </div>
      {spec.help ? <p className="text-xs text-ink-600">{spec.help}</p> : null}
      <p className="text-2xs text-ink-500">
        {`Fonte: ${spec.source || 'nao informada'}`}
        {spec.unit ? `; Unidade: ${spec.unit}` : ''}
        {spec.nullable ? '; Aceita em branco' : ''}
        {spec.rules && spec.rules.length > 0
          ? `; Regras: ${spec.rules.join(', ')}`
          : ''}
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className={titulo}>Versao ativa</span>
          <div className="text-sm text-ink-900">
            {ativa?.values ? (
              <ValorLegivel valor={valorAtivo} rotulos={rotulos} unidade={spec.unit} />
            ) : (
              <span className="text-ink-500 italic">Sem versao ativa</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className={titulo}>Rascunho</span>
          <div className="text-sm text-ink-900">
            {!rascunho?.values ? (
              <span className="text-ink-500 italic">Sem rascunho aberto</span>
            ) : editavel ? (
              <EditorDoParametro
                spec={spec}
                inicial={valorRascunho ?? null}
                rotulos={rotulos}
                regras={regras}
                onChange={onChange}
              />
            ) : (
              <ValorLegivel
                valor={valorRascunho}
                rotulos={rotulos}
                unidade={spec.unit}
              />
            )}
          </div>
        </div>
      </div>
      {/* No mapa e na tabela, o erro de cada celula fica na celula; aqui vai qual
          linha ou chave esta errada. Nos demais, o proprio campo ja mostra. */}
      {pendente && !pendente.ok && (spec.kind === 'mapa' || spec.kind === 'tabela') ? (
        <p className="text-2xs font-medium text-danger-700">{pendente.erro}</p>
      ) : null}
      {erroDoServidor ? (
        <p role="alert" className="text-2xs font-medium text-danger-700">
          {erroDoServidor}
        </p>
      ) : null}
    </section>
  );
}
